import { Trans, useLingui } from '@lingui/react/macro'
import { Checkbox } from 'designSystem/components/Checkbox'
import { Input } from 'designSystem/components/Input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'designSystem/components/Select'
import { useSentinelOverlayMap } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  BucketGridSetting,
  DEFAULT_BUCKET_GRID,
  inferBucketInterval,
} from 'features/timeSeries/utils/bucketGrid'
import { FC, ReactNode, useCallback } from 'react'
import styled from 'styled-components'
import {
  ResampleAggregation,
  ResampleConfig,
  ResampleIntervalUnit,
  SmoothingConfig,
  SmoothingMethod,
  ThresholdCondition,
  ThresholdFilterConfig,
} from 'types'

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.75rem;
  min-width: 15rem;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const SectionHead = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 4.5rem 1fr;
  align-items: center;
  gap: 0.375rem;
`

const RowLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.68rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SMALL_INPUT = { height: '1.75rem', fontSize: '0.75rem' }

const defaultSmoothing = (sensorId: string): SmoothingConfig => ({
  type: 'smoothing',
  sensorId,
  enabled: true,
  method: 'avg',
  basis: 'duration',
  windowSeconds: 30 * 60,
  windowSize: 6,
  alignment: 'trailing',
  display: 'overlay',
  requireFullWindow: true,
})

const defaultFilter = (sensorId: string): ThresholdFilterConfig => ({
  type: 'threshold_filter',
  sensorId,
  enabled: true,
  condition: 'above',
  value1: 0,
  value2: null,
})

const defaultResample = (
  sensorId: string,
  grid: BucketGridSetting
): ResampleConfig => ({
  type: 'resample',
  sensorId,
  enabled: true,
  interval: grid.interval,
  intervalUnit: grid.intervalUnit,
  aggregationOp: grid.aggregationOp,
})

const INTERVAL_UNITS: ResampleIntervalUnit[] = [
  'seconds',
  'minutes',
  'hours',
  'days',
]

const numberOr = (raw: string, fallback: number): number => {
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** True when any of this pen's transforms is enabled. */
export const usePenHasTransform = (sensorId: string): boolean => {
  const { thresholdFilters, smoothingConfigs, resampleConfigs } =
    useScatterplotContext()

  return (
    (thresholdFilters[sensorId]?.enabled ?? false) ||
    (smoothingConfigs[sensorId]?.enabled ?? false) ||
    (resampleConfigs[sensorId]?.enabled ?? false)
  )
}

export const PenTransformsPopover: FC<{
  sensorId: string
  trigger: ReactNode
  side?: 'left' | 'right' | 'top' | 'bottom'
}> = ({ sensorId, trigger, side = 'left' }) => {
  const overlaysByTarget = useSentinelOverlayMap({ includeHidden: true })
  const overlay = overlaysByTarget.get(sensorId)
  const inferred = overlay ? inferBucketInterval(overlay.xs) : null

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align='start'
        // both hosts sit inside rows that click to toggle visibility
        onClick={(event) => event.stopPropagation()}>
        <PenTransformControls
          sensorId={sensorId}
          suggestedGrid={
            inferred ? { ...DEFAULT_BUCKET_GRID, ...inferred } : undefined
          }
        />
      </PopoverContent>
    </Popover>
  )
}

export interface PenTransformControlsProps {
  sensorId: string
  /** Grid to offer when buckets are switched on. */
  suggestedGrid?: BucketGridSetting
}

export const PenTransformControls: FC<PenTransformControlsProps> = ({
  sensorId,
  suggestedGrid = DEFAULT_BUCKET_GRID,
}) => {
  const { t } = useLingui()

  const {
    thresholdFilters,
    setThresholdFilters,
    smoothingConfigs,
    setSmoothingConfigs,
    resampleConfigs,
    setResampleConfigs,
  } = useScatterplotContext()

  const filter = thresholdFilters[sensorId]
  const smoothing = smoothingConfigs[sensorId]
  const resample = resampleConfigs[sensorId]

  const patchFilter = useCallback(
    (patch: Partial<ThresholdFilterConfig>) => {
      setThresholdFilters((previous) => ({
        ...previous,
        [sensorId]: {
          ...(previous[sensorId] ?? defaultFilter(sensorId)),
          ...patch,
        },
      }))
    },
    [sensorId, setThresholdFilters]
  )

  const patchSmoothing = useCallback(
    (patch: Partial<SmoothingConfig>) => {
      setSmoothingConfigs((previous) => ({
        ...previous,
        [sensorId]: {
          ...(previous[sensorId] ?? defaultSmoothing(sensorId)),
          ...patch,
        },
      }))
    },
    [sensorId, setSmoothingConfigs]
  )

  const patchResample = useCallback(
    (patch: Partial<ResampleConfig>) => {
      setResampleConfigs((previous) => ({
        ...previous,
        [sensorId]: {
          ...(previous[sensorId] ?? defaultResample(sensorId, suggestedGrid)),
          ...patch,
        },
      }))
    },
    [sensorId, setResampleConfigs, suggestedGrid]
  )

  const filterOn = filter?.enabled ?? false
  const smoothingOn = smoothing?.enabled ?? false
  const resampleOn = resample?.enabled ?? false

  return (
    <Panel>
      <Section>
        <SectionHead>
          <Trans>Filter</Trans>
          <Checkbox
            checked={filterOn}
            aria-label={t`Filter this pen`}
            onCheckedChange={(checked) =>
              patchFilter({ enabled: checked === true })
            }
          />
        </SectionHead>
        {filterOn && (
          <>
            <Row>
              <RowLabel>
                <Trans>Keep</Trans>
              </RowLabel>
              <Select
                value={filter?.condition ?? 'above'}
                onValueChange={(value) =>
                  patchFilter({ condition: value as ThresholdCondition })
                }>
                <SelectTrigger style={SMALL_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='above'>above</SelectItem>
                  <SelectItem value='below'>below</SelectItem>
                  <SelectItem value='between'>between</SelectItem>
                  <SelectItem value='stability'>stable within</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row>
              <RowLabel>
                {filter?.condition === 'stability' ? t`CV %` : t`Value`}
              </RowLabel>
              <Pair>
                <Input
                  type='number'
                  style={SMALL_INPUT}
                  value={String(filter?.value1 ?? 0)}
                  aria-label={t`Filter value`}
                  onChange={(event) =>
                    patchFilter({
                      value1: numberOr(event.target.value, filter?.value1 ?? 0),
                    })
                  }
                />
                {(filter?.condition === 'between' ||
                  filter?.condition === 'stability') && (
                  <Input
                    type='number'
                    style={SMALL_INPUT}
                    value={String(filter?.value2 ?? 0)}
                    aria-label={
                      filter?.condition === 'between'
                        ? t`Filter upper value`
                        : t`Stability window`
                    }
                    onChange={(event) =>
                      patchFilter({
                        value2: numberOr(
                          event.target.value,
                          filter?.value2 ?? 0
                        ),
                      })
                    }
                  />
                )}
              </Pair>
            </Row>
            <Hint>
              <Trans>
                Excluded points are marked with a red x. Other pens keep every
                sample.
              </Trans>
            </Hint>
          </>
        )}
      </Section>

      <Section>
        <SectionHead>
          <Trans>Rolling average</Trans>
          <Checkbox
            checked={smoothingOn}
            aria-label={t`Rolling average for this pen`}
            onCheckedChange={(checked) =>
              patchSmoothing({ enabled: checked === true })
            }
          />
        </SectionHead>
        {smoothingOn && (
          <>
            <Row>
              <RowLabel>
                <Trans>Window</Trans>
              </RowLabel>
              <Pair>
                <Input
                  type='number'
                  min={1}
                  style={SMALL_INPUT}
                  aria-label={t`Rolling window`}
                  value={String(
                    smoothing?.basis === 'samples'
                      ? (smoothing?.windowSize ?? 6)
                      : Math.round((smoothing?.windowSeconds ?? 1800) / 60)
                  )}
                  onChange={(event) => {
                    const value = numberOr(event.target.value, 1)
                    patchSmoothing(
                      smoothing?.basis === 'samples'
                        ? { windowSize: Math.max(1, Math.round(value)) }
                        : { windowSeconds: Math.max(1, value) * 60 }
                    )
                  }}
                />
                <Select
                  value={smoothing?.basis === 'samples' ? 'samples' : 'minutes'}
                  onValueChange={(value) =>
                    patchSmoothing(
                      value === 'samples'
                        ? { basis: 'samples' }
                        : { basis: 'duration' }
                    )
                  }>
                  <SelectTrigger style={SMALL_INPUT}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='minutes'>minutes</SelectItem>
                    <SelectItem value='samples'>samples</SelectItem>
                  </SelectContent>
                </Select>
              </Pair>
            </Row>
            <Row>
              <RowLabel>
                <Trans>Method</Trans>
              </RowLabel>
              <Select
                value={smoothing?.method ?? 'avg'}
                onValueChange={(value) =>
                  patchSmoothing({ method: value as SmoothingMethod })
                }>
                <SelectTrigger style={SMALL_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='avg'>average</SelectItem>
                  <SelectItem value='median'>median</SelectItem>
                  <SelectItem value='min'>minimum</SelectItem>
                  <SelectItem value='max'>maximum</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row>
              <RowLabel>
                <Trans>Window ends</Trans>
              </RowLabel>
              <Select
                value={
                  smoothing?.alignment === 'centered'
                    ? 'centered'
                    : smoothing?.alignment === 'trailingShifted'
                      ? 'trailingShifted'
                      : 'trailing'
                }
                onValueChange={(value) =>
                  patchSmoothing({
                    alignment: value as SmoothingConfig['alignment'],
                  })
                }>
                <SelectTrigger style={SMALL_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='trailing'>at this point</SelectItem>
                  <SelectItem value='trailingShifted'>
                    at the previous point
                  </SelectItem>
                  <SelectItem value='centered'>centered</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row>
              <RowLabel>
                <Trans>Draw as</Trans>
              </RowLabel>
              <Select
                value={smoothing?.display === 'replace' ? 'replace' : 'overlay'}
                onValueChange={(value) =>
                  patchSmoothing({
                    display: value as SmoothingConfig['display'],
                  })
                }>
                <SelectTrigger style={SMALL_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='overlay'>dashed overlay</SelectItem>
                  <SelectItem value='replace'>replace the pen</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Hint>
              {smoothing?.alignment === 'trailingShifted'
                ? t`Only past points, like a Sentinel target’s own history.`
                : t`This point and the ones before it, like a driver feature.`}
            </Hint>
          </>
        )}
      </Section>

      <Section>
        <SectionHead>
          <Trans>Training buckets</Trans>
          <Checkbox
            checked={resampleOn}
            aria-label={t`Training buckets for this pen`}
            onCheckedChange={(checked) =>
              patchResample({ enabled: checked === true })
            }
          />
        </SectionHead>
        {resampleOn && (
          <>
            <Row>
              <RowLabel>
                <Trans>Bucket</Trans>
              </RowLabel>
              <Pair>
                <Input
                  type='number'
                  min={1}
                  style={SMALL_INPUT}
                  aria-label={t`Bucket interval`}
                  value={String(resample?.interval ?? suggestedGrid.interval)}
                  onChange={(event) =>
                    patchResample({
                      interval: Math.max(
                        1,
                        Math.round(numberOr(event.target.value, 10))
                      ),
                    })
                  }
                />
                <Select
                  value={resample?.intervalUnit ?? suggestedGrid.intervalUnit}
                  onValueChange={(value) =>
                    patchResample({
                      intervalUnit: value as ResampleIntervalUnit,
                    })
                  }>
                  <SelectTrigger style={SMALL_INPUT}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Pair>
            </Row>
            <Row>
              <RowLabel>
                <Trans>Aggregate</Trans>
              </RowLabel>
              <Select
                value={resample?.aggregationOp ?? suggestedGrid.aggregationOp}
                onValueChange={(value) =>
                  patchResample({
                    aggregationOp: value as ResampleAggregation,
                  })
                }>
                <SelectTrigger style={SMALL_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='avg'>mean</SelectItem>
                  <SelectItem value='min'>minimum</SelectItem>
                  <SelectItem value='max'>maximum</SelectItem>
                  <SelectItem value='sum'>sum</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Hint>
              <Trans>
                Fetched on the same bucket grid the models train on, drawn
                stepped.
              </Trans>
            </Hint>
          </>
        )}
      </Section>
    </Panel>
  )
}
